import { useCustom } from '@refinedev/core';
import { useEffect, useRef, useState } from 'react';

/** typing changes the text on every key; draw once the person pauses (the API also limits drawings per minute) */
const SETTLE_MS = 600;

/**
 * The portal's diagram (F-199): the UML text the page built, drawn by our own API (`diagramImage`, a POST
 * through the portal's authenticated data provider — the text is never in a URL). `uri` is a
 * `data:image/png;base64,…` URI, because an `<img src>` cannot send the Firebase token. While a changed text
 * is being drawn the previous picture stays. `error` means it could not be drawn; the page then shows the
 * person's own text instead (never another service).
 */
export const useDiagramImage = (source: string | null | undefined) => {
  const wanted = source || '';
  const [settled, setSettled] = useState(wanted);
  useEffect(() => {
    if (wanted === settled) return undefined;
    const id = setTimeout(() => setSettled(wanted), SETTLE_MS);
    return () => clearTimeout(id);
  }, [wanted, settled]);

  const { data, isFetching, isError } = useCustom<Record<string, unknown>>({
    url: '',
    method: 'get',
    meta: {
      operation: 'diagramImage',
      fields: [],
      variables: {
        source: { name: 'source', type: 'String!', value: settled },
      },
    },
    // a picture that cannot be drawn is answered IN PLACE by the person's own words: no error toast
    errorNotification: false,
    queryOptions: {
      enabled: !!settled,
      retry: false,
      staleTime: Infinity,
      // the text is the key; a new text is a new drawing
      queryKey: ['diagramImage', settled],
    },
  });

  // Refine's graphql provider UNWRAPS the operation: `data.data` is the string itself; the wrapped shape is read too
  const payload = data?.data as unknown;
  const png =
    typeof payload === 'string'
      ? payload
      : (payload as { diagramImage?: string } | undefined)?.diagramImage;
  const lastUri = useRef<string | null>(null);
  if (png) lastUri.current = `data:image/png;base64,${png}`;
  const pending = !!wanted && (isFetching || wanted !== settled);

  return {
    uri: png || pending ? lastUri.current : null,
    loading: pending && !lastUri.current,
    error: !pending && !png && isError,
  };
};
